import { apiFetch, ApiNetworkError } from './apiClient';
import { defaultErrorMessage } from './httpRequest';

/**
 * #1051 (ADR 0019 part 2) -- the signed-in user's organisations, their members, invitations and
 * settings. The personal tenant is implicit: it never appears in these lists.
 */
export type TenantRole = 'VIEWER' | 'EDITOR' | 'ADMINISTRATOR';
export type ClaimVisibility = 'ORGANISATION' | 'CREATOR';

export interface Tenant {
  readonly id: string;
  readonly type: 'ORG' | 'SUB_ORG';
  readonly name: string;
  readonly parentId: string | null;
  /** The signed-in user's role in it. */
  readonly role: TenantRole;
  /** ADR 0019 D4 (O2): who sees members' molecule claims. */
  readonly claimVisibility: ClaimVisibility;
  /** ADR 0019 D7: whether structures may be looked up in outside sources such as PubChem. */
  readonly outsideLookups: boolean;
}

export interface TenantMember {
  readonly userId: string;
  readonly displayName: string;
  readonly role: TenantRole;
}

export interface TenantInvitation {
  readonly id: string;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly email: string;
  readonly role: TenantRole;
  readonly invitedBy: string;
  readonly createdAt: string;
  /**
   * Only on the invitee's own list: whether they can accept it yet. Owner (2026-10-23): an
   * invitation shows before the email is verified; joining waits for the verification.
   */
  readonly canAccept?: boolean;
}

export class TenantApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'TenantApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    // Organisation management always acts as the user themselves, whatever tenant is active.
    response = await apiFetch(path, init);
  } catch (error) {
    throw new TenantApiError(
      error instanceof ApiNetworkError
        ? `Unable to reach the organisations API: ${error.message}`
        : 'Unable to reach the organisations API.',
      0
    );
  }
  if (response.status === 401) throw new TenantApiError('Session expired. Redirecting to sign in…', 401);
  if (!response.ok) throw new TenantApiError(await defaultErrorMessage(response, 'organisations'), response.status);
  return (response.status === 204 ? undefined : await response.json()) as T;
}

function json(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

export function listMyTenants(): Promise<readonly Tenant[]> {
  return request('/tenants');
}

/** P1: any signed-in user; they become its administrator. With a parent: a sub-organisation. */
export function createOrganisation(name: string, parentId?: string): Promise<Tenant> {
  return request('/tenants', json('POST', { name, parentId: parentId ?? null }));
}

export function listMembers(tenantId: string): Promise<readonly TenantMember[]> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/members`);
}

export function changeMemberRole(tenantId: string, userId: string, role: TenantRole): Promise<void> {
  return request(
    `/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    json('PUT', { role })
  );
}

/** An administrator removes a member; with your own id, you leave. */
export function removeMember(tenantId: string, userId: string): Promise<void> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE'
  });
}

export function updateTenantSettings(
  tenantId: string,
  claimVisibility: ClaimVisibility,
  outsideLookups: boolean
): Promise<Tenant> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/settings`, json('PUT', { claimVisibility, outsideLookups }));
}

/** P2: an administrator invites by email; the invitee accepts after signing in with it. */
export function inviteMember(tenantId: string, email: string, role: TenantRole): Promise<TenantInvitation> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/invitations`, json('POST', { email, role }));
}

export function listTenantInvitations(tenantId: string): Promise<readonly TenantInvitation[]> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/invitations`);
}

export function revokeInvitation(tenantId: string, invitationId: string): Promise<void> {
  return request(`/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}`, {
    method: 'DELETE'
  });
}

/** Pending invitations to the signed-in user's verified email. */
export function listMyInvitations(): Promise<readonly TenantInvitation[]> {
  return request('/me/invitations');
}

export function respondToInvitation(invitationId: string, accept: boolean): Promise<void> {
  return request(`/me/invitations/${encodeURIComponent(invitationId)}/${accept ? 'accept' : 'decline'}`, {
    method: 'POST'
  });
}
